import { NostoPlacementsChangedEvent, ProductSwappedEvent } from '@theme/events';
import { nostoLogger, reloadNostoPlacements } from '@theme/nosto-placements';

/**
 * Re-tags the PDP for Nosto and reloads its recommendation placements after the phase 3 AJAX color
 * swap (`product:swapped`) — the placements live in their own section (sections/
 * product-recommendations-nosto.liquid) so the swap's morph never touches them, and Nosto's own
 * onsite script only reads product context once, at initial page load.
 *
 * The Session API call itself, and its feature-detection posture, live in `@theme/nosto-placements`
 * (shared with the cart confirmation layer's cross-sell, which faces the same problem).
 */

const debug = nostoLogger('nosto-pdp-recommendations');

/**
 * IDs of the `.nosto_element` containers currently in the page, read from the DOM rather than
 * hardcoded: `placement_id` is an editor setting on each `_nosto-placement` block, so it can be
 * repointed at any time to match the "Div ID" configured in the Nosto dashboard (it already moved
 * once, from provisional `nosto-pdp-*` names to Nosto's own `productpage-nosto-*` convention).
 * Reading the DOM keeps the two in sync by construction.
 *
 * Only page placements are collected. The cart confirmation layer's own container is marked with
 * `data-nosto-cart-placement` instead, deliberately: it is mounted in the header, so it is present
 * on the PDP too, and it must be reloaded against the *added* product, never against the swapped
 * color.
 *
 * @returns {string[]}
 */
function placementIds() {
  return Array.from(document.querySelectorAll('[data-nosto-placement]'))
    .map((element) => element.id)
    .filter(Boolean);
}

/**
 * Two triggers, deliberately not coalesced.
 *
 * `product:swapped` covers the placements already in the page — this section is never morphed by
 * the color swap, so its containers survive and can be refilled straight away.
 * `nosto:placements-changed` covers containers a re-rendering section has just replaced with empty
 * ones (the cross-sell Nosto fallback), which only exist once its Section Rendering fetch lands.
 *
 * A single color change therefore costs up to two reloads. Debouncing them into one would mean
 * guessing how long that fetch takes: too short and we read the DOM before the new container
 * exists, too long and the visible placements stay stale for no reason. Both calls read the DOM at
 * call time, are feature-detected end to end, and degrade to a no-op — so the redundant one is
 * cheap, while a mistimed single one would leave an empty zone.
 */
document.addEventListener(ProductSwappedEvent.eventName, (event) => {
  if (!(event instanceof ProductSwappedEvent)) return;
  reloadNostoPlacements(event.detail.productId, placementIds(), debug);
});

document.addEventListener(NostoPlacementsChangedEvent.eventName, (event) => {
  if (!(event instanceof NostoPlacementsChangedEvent)) return;
  reloadNostoPlacements(event.detail.productId, placementIds(), debug);
});

/**
 * Stand-in cards (snippets/nosto-fallback-cards.liquid) sit under a skeleton until Nosto has had its
 * say (assets/base.css). Nosto's own `postrender` decides, not a timer: a slow connection only delays
 * the skeleton, it never shows a first set of products swapped for Nosto's a moment later. A
 * container Nosto rendered without filling is left with no recommendation, so its stand-in shows.
 */
const FALLBACK_SAFETY_DELAY = 8000;

/** @param {string[]} [filled] - Div IDs Nosto has just filled, whose stand-in is already gone. */
function showFallbacks(filled = []) {
  for (const container of document.querySelectorAll('.nosto_element:not([data-nosto-fallback-shown])')) {
    if (!filled.includes(container.id)) container.setAttribute('data-nosto-fallback-shown', '');
  }
}

/**
 * Nosto never answering (blocked, script error) must not leave a skeleton forever. Counted from the
 * page `load`, so a slow connection gets the whole page download before the clock starts, and re-armed
 * when a color swap brings in a fresh container.
 */
function armFallbackSafety() {
  setTimeout(() => showFallbacks(), FALLBACK_SAFETY_DELAY);
}

/**
 * There is no inline `nostojs` stub: the app embed injects `nosto.js` as an async script, which on a
 * cold load runs after this module. It does delay `load`, though, so that is when its absence means
 * something. A late `postrender` listener still gets the last render replayed.
 */
function watchNosto() {
  if (!window.nostojs) {
    showFallbacks();
    return;
  }

  window.nostojs((api) => api.listen?.('postrender', ({ filledElements }) => showFallbacks(filledElements)));
  armFallbackSafety();
}

if (document.readyState === 'complete') watchNosto();
else window.addEventListener('load', watchNosto, { once: true });

document.addEventListener(NostoPlacementsChangedEvent.eventName, armFallbackSafety);
